import {
  CLUB_ROOM_NAME,
  IGLOO_ROOM_NAME,
  RoomState,
  SOCIAL_ROOM_ID,
  VOLLEY_ROOM_NAME,
  VolleyState,
  type JoinOptions,
  type RoomId,
  type SocialJoinOptions,
  type VolleyJoinOptions,
} from "@clubap/shared";
import { Client, type Room } from "colyseus.js";
import { SERVER_URL } from "./backend.js";

const client = new Client(SERVER_URL);

/**
 * Cada sala del catálogo está registrada en el servidor con su propio id, así que
 * el nombre de matchmaking *es* el id de la sala.
 *
 * Pasar RoomState acá es lo que permite decodificar los patches binarios a
 * instancias tipadas.
 */
export function joinRoom(roomId: RoomId, options: JoinOptions): Promise<Room<RoomState>> {
  return client.joinOrCreate<RoomState>(roomId, options, RoomState);
}

/**
 * Tu iglú, por NOMBRE y agrupado por dueño.
 *
 * No se entra por una puerta, y no es un olvido: una puerta sólo sabe llevar a un id del
 * catálogo, y los iglús son uno por cuenta. Por eso esto se llama desde el carnet.
 *
 * El `ownerId` es lo que agrupa el matchmaking (`filterBy` en el servidor), así que dos
 * pestañas tuyas caen en la misma instancia. No autoriza nada: quién puede entrar lo
 * decide `onAuth` con el token, y hoy sólo entra el dueño.
 *
 * `returnRoomId` es la sala a la que lleva la puerta de salida — de dónde saliste, para
 * que volver te deje donde estabas y no en el otro extremo del campus.
 */
export function joinIgloo(
  ownerId: string,
  returnRoomId: RoomId,
  options: JoinOptions,
): Promise<Room<RoomState>> {
  return client.joinOrCreate<RoomState>(
    IGLOO_ROOM_NAME,
    { ...options, ownerId, returnRoomId },
    RoomState,
  );
}

/**
 * La sala de un club, agrupada por club.
 *
 * Mismo camino que el iglú y por la misma razón —no es un `RoomId`, así que no hay puerta
 * que lleve a ella— pero el filtro es el CLUB y no la cuenta: acá sí queremos que todos
 * los miembros caigan en la misma instancia. Quién puede entrar lo decide `onAuth`.
 */
export function joinClubRoom(
  clubId: string,
  returnRoomId: RoomId,
  options: JoinOptions,
): Promise<Room<RoomState>> {
  return client.joinOrCreate<RoomState>(
    CLUB_ROOM_NAME,
    { ...options, clubId, returnRoomId },
    RoomState,
  );
}

/** El servidor rechaza con 403 la sala de un club del que no eres miembro. */
export const isClubPrivate = isClosedRoom;

/**
 * El canal social, por ID y no por nombre.
 *
 * `joinById` apunta a LA instancia que el servidor creó al arrancar. Con
 * `joinOrCreate` un canal lleno haría nacer un segundo, y entonces la mitad de las
 * cuentas recibiría los avisos y la otra mitad no.
 *
 * Sin esquema raíz a propósito: el canal social no tiene estado sincronizado — todo
 * lo que manda es privado y va dirigido a una cuenta.
 */
export function joinSocial(options: SocialJoinOptions): Promise<Room<unknown>> {
  return client.joinById<unknown>(SOCIAL_ROOM_ID, options);
}

/**
 * Una cancha de volley, por NOMBRE y con `joinOrCreate`.
 *
 * Es el caso contrario al canal social, y por el mismo razonamiento al revés. Allá una
 * segunda instancia partiría la población, así que se entra por id a la única que hay.
 * Acá una segunda instancia es justo lo que queremos: las canchas son de dos, y quien
 * llegue cuando la primera esté llena tiene que estrenar otra. Colyseus la bloquea sola
 * al llegar a `maxClients`.
 */
export function joinVolley(options: VolleyJoinOptions): Promise<Room<VolleyState>> {
  return client.joinOrCreate<VolleyState>(VOLLEY_ROOM_NAME, options, VolleyState);
}

/**
 * Una cancha de PRÁCTICA contra la computadora: `create`, no `joinOrCreate`.
 *
 * La diferencia importa. Con `joinOrCreate`, pedir un partido contra la máquina te metía
 * en la cancha de alguien que esperaba rival de verdad — y esa persona acabaría
 * jugando el partido que pidió, pero por accidente. `create` estrena siempre una cancha
 * propia, que el servidor cierra en cuanto sienta al bot.
 */
export function createVolleyPractice(options: VolleyJoinOptions): Promise<Room<VolleyState>> {
  return client.create<VolleyState>(VOLLEY_ROOM_NAME, { ...options, vsBot: true }, VolleyState);
}

/** El servidor rechaza el join con 401 cuando el token venció o es inválido. */
export function isAuthFailure(cause: unknown): boolean {
  return codeOf(cause) === 401;
}

/**
 * Y con 403 cuando la sala no está abierta al juego normal.
 *
 * Jugando no se llega: la puerta ni se dibuja. Pasa cuando una pestaña vieja pide una
 * sala que dejó de estar abierta, o cuando a una cuenta dev se le quita la marca
 * mientras está dentro. Reintentar ahí no sirve — hay que volver al campus abierto.
 */
export function isClosedRoom(cause: unknown): boolean {
  return codeOf(cause) === 403;
}

function codeOf(cause: unknown): number | undefined {
  return typeof cause === "object" && cause !== null
    ? (cause as { code?: number }).code
    : undefined;
}
